import {Injectable, OnDestroy} from '@angular/core';
import {combineLatest, concatMap, forkJoin, Observable, of, Subject} from "rxjs";
import {Series} from "../../_models/series";
import {first, map, takeUntil, tap} from 'rxjs/operators';
import {ListResponse} from '../../services/api/response-types';
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {SeriesDataService} from "../../services/series_data.service";
import {SeriesDataTableData} from "../../_typing/components/series-data-table";
import {TimePeriod} from "../../_typing/components/time-period";
import {KeyMap, ModelID} from "../../_typing/generic-types";
import {
    SeriesDataSeriesGroupDict,
    SeriesDataTableConfig,
    SeriesDataTableHeaderRows,
    SeriesGroupConfig
} from "../../_typing/config/series-data-table";
import {SeriesDataTableFormService} from "../../forms/series-data-table-form/series-data-table-form.service";


@Injectable()
export class SeriesDataTableService implements OnDestroy {
    private readonly onDestroy = new Subject<void>();
    public readonly dataReady: Subject<SeriesDataTableData> = new Subject<SeriesDataTableData>();

    table_data: SeriesDataTableData = {
        series_list: [],
        series_data: null,
        series_missing: null,
        series_summary: null,
        group_dict: {}
    };
    dtp: IDateTimePeriod;
    public seriesDataTableMenuOptions: string[] = ['bold', 'italic', 'size', 'resize', 'fit_content', 'colour', 'background_colour', 'decimals', 'align'];

    constructor(private seriesDataService: SeriesDataService,
                private seriesDataTableFormService: SeriesDataTableFormService) {

    }

    getTimes(dtp: IDateTimePeriod, dateTimes: string[]): KeyMap<TimePeriod> {
        let previousPeriodEnd: string | null = null;
        const times: KeyMap<TimePeriod> = {};
        dateTimes.forEach((dateTime: string) => {
            let date_counter: Date = new Date(dateTime);
            if (dtp.sample_period.name.toLowerCase() === 'month') {
                date_counter.setMonth(date_counter.getMonth() - 1);
                if (date_counter < dtp.start || dateTimes.length === 1) {
                    date_counter = dtp.start;
                }
            } else {
                date_counter['addHours'](dtp.sample_period.hours * -1);
            }
            const periodStart: string = previousPeriodEnd || date_counter.toISOString();
            previousPeriodEnd = dateTime;
            times[dateTime] = {
                period_start: periodStart,
                period_end: dateTime,
                title: dtp.sample_period.format(new Date(dateTime), dtp)
            };
        });
        return times;
    }

    getSeriesData(config, dtp: IDateTimePeriod) {
        this.seriesDataTableFormService.fullSeriesListSubjectConnect();
        this.seriesDataTableFormService.$fullSeriesList.pipe(
            tap(seriesList => {
                this.table_data.series_list = (this.table_data.series_list || []).concat(seriesList)
            })
        ).subscribe(); //This obs complete internally

        const $updateSeries = this.seriesDataTableFormService.getSeriesForProcesses(config, true);
        $updateSeries.pipe(concatMap(newSeriesMap => {
            config.process_series_map = newSeriesMap;
            const list = this.seriesList(config);
            const seriesIds = list.map(s => s.id);

            const join = [this.getData(dtp, seriesIds, config.gss?.shift_type_id)];
            if (config.gss?.columns?.length > 0 && ['top', 'bottom'].includes(config.show_gss)) {
                join.push(this.getSeriesSummary(dtp, seriesIds, config.gss.columns, config.gss?.estimate_type, config.gss?.shift_type_id))
            }
            return forkJoin(join).pipe(first(), takeUntil(this.onDestroy))
        })).subscribe(() => {
            const list = this.seriesList(config);
            this.table_data.group_dict = this.getGroupDict(config, list);
            this.dataReady.next(this.table_data);
        });
    }

    getGroupDict(config: SeriesDataTableConfig, seriesList: Partial<SeriesGroupConfig>[]): SeriesDataSeriesGroupDict {
        let groupDict: SeriesDataSeriesGroupDict = {}
        if (config.header_rows?.custom_groups) {
            seriesList.forEach(s => {
                const group = s.group || "";
                groupDict[group] = (groupDict[group] || []).concat([s]);
            })
            return groupDict;
        }
        let processDict = {};
        config.process_list.forEach(p => processDict[p.id] = p.attributes.name);
        Object.keys(config.process_series_map).forEach(k => groupDict[processDict[k]] = config.process_series_map[k]);
        return groupDict;
    }

    seriesList(config: SeriesDataTableConfig): Partial<SeriesGroupConfig>[] {
        const list = Object.values(config.process_series_map)
            .reduce((prev: Partial<SeriesGroupConfig>[], curr: Partial<SeriesGroupConfig>[]) => prev = prev.concat(curr), []);
        return list;
    }

    private getSeriesBySeriesIds(series_ids: string[]): Observable<ListResponse<Series>> {
        return this.seriesDataService.getSeriesListByIds(series_ids).pipe(first(), takeUntil(this.onDestroy),
            tap(result => {
                this.table_data.series_list = result.data;
            }))
    }

    private getData(dtp: IDateTimePeriod, series_ids: string[], shiftTypeId: number): Observable<any> {
        return this.seriesDataService.getSeriesData(dtp, series_ids, null, [], shiftTypeId, 'records')
            .pipe(tap(result => {
                this.table_data.series_data = result;

                this.table_data.series_data = {};
                result.data.forEach(s => {
                    this.table_data.series_data[s.index] = s;
                })

                this.table_data.series_missing = {};
                result.missing_values.forEach(s => {
                    this.table_data.series_missing[s.index] = s;
                });
            }));
    }

    private getSeriesSummary(dtp: IDateTimePeriod, series_ids: string[], columns: string[], estimateTypes: string[], shiftTypeId?: number): Observable<any> {
        return this.seriesDataService.getSeriesSummary(dtp, series_ids, null, columns, estimateTypes, shiftTypeId)
            .pipe(tap(result => {
                this.table_data.series_summary = {};
                result.forEach(s => {
                    this.table_data.series_summary[s.ID] = s;
                });
            }));
    }

    public showTimeColumn(config) {
        /**This function is just for working out where to display the time column since all header_rows are optional**/
        let dict: SeriesDataTableHeaderRows = {process: config.header_rows.process || config.header_rows.custom_groups};
        /**Just make the variable names shorter to work with8*/
        const [process, custom_groups, alias, unit, name, description, edit] = ['process', 'custom_groups', 'alias', 'unit', 'name', 'description', 'edit'].map(i => {
            return config.header_rows[i]
        })
        if (!(process || custom_groups)) {
            dict['name'] = true;
        }
        if (!(process || custom_groups || name)) {
            dict['description'] = true;
        }
        if (!(process || custom_groups || name || description)) {
            dict['alias'] = true;
        }
        if (!(process || custom_groups || name || description || alias)) {
            dict['unit'] = true;
        }
        if (!(process || custom_groups || name || description || alias || unit)) {
            dict['edit'] = true;
        }
        return dict;
    }


    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
